import { useCallback } from 'react';
import { useRequiredParamsSetter } from '../context/TargetingSettingsContext';
import type {
  Element,
  RequiredParamsType,
  RequiredTargetingSectionKeysType,
  TargetingElementKeysType,
} from '../context/types';
import { sectionElementReducer } from './reducer';
import type { SectionElementOperation } from './types';
import { useElementOperations } from './useElementOperations';

export const useTargetingSelectionActions = () => {
  const set = useRequiredParamsSetter();
  const {
    enableElement,
    disableElement,
    selectElement,
    disableElementByKey,
    selectElementByKey,
    enableElementByKey,
    deselectElementByKey,
    deselectElement,
  } = useElementOperations();

  const toggleMainElementOptionSelection = useCallback((element: Element): Element => {
    return {
      ...element,
      mainOption: {
        ...element.mainOption,
        isSelected: !element.mainOption.isSelected,
      },
    };
  }, []);

  const toggleElementSelection = useCallback(
    (sectionKey: RequiredTargetingSectionKeysType, elementIndex: number) => {
      set((prev: RequiredParamsType) =>
        (() => {
          const updatedElements = [...prev.sections[sectionKey].elements];
          updatedElements[elementIndex] = toggleMainElementOptionSelection(
            prev.sections[sectionKey].elements[elementIndex]
          );

          return {
            ...prev,
            sections: {
              ...prev.sections,
              [sectionKey]: {
                ...prev.sections[sectionKey],
                elements: updatedElements,
              },
            },
          };
        })()
      );
    },
    [toggleMainElementOptionSelection, set]
  );

  const blockSection = useCallback(
    (elements: Element[], key: TargetingElementKeysType | null): Element[] => {
      return elements.map(el => (el.mainOption.key === key ? selectElement(el) : disableElement(el)));
    },
    [disableElement, selectElement]
  );

  const enableSection = useCallback(
    (elements: Element[], key: TargetingElementKeysType | null): Element[] => {
      return elements.map(el => (el.mainOption.key === key ? deselectElement(el) : enableElement(el)));
    },
    [enableElement, deselectElement]
  );

  const toggleSubElementSelection = useCallback(
    (sectionKey: string, elementIndex: number, subElementIndex: number | undefined) => {
      if (subElementIndex === undefined || subElementIndex === null) return;

      set((prev: RequiredParamsType) => {
        const selectedElement: Element = prev.sections[sectionKey].elements[elementIndex];
        if (!selectedElement.subOptions) return prev;

        const updatedSubOptions = [...selectedElement.subOptions];
        updatedSubOptions[subElementIndex] = {
          ...updatedSubOptions[subElementIndex],
          isSelected: !updatedSubOptions[subElementIndex].isSelected,
        };

        selectedElement.mainOption.isSelected = updatedSubOptions.some(subOption => subOption.isSelected);

        const updatedElement = {
          ...selectedElement,
          subOptions: updatedSubOptions,
        };

        const updatedElements = [...prev.sections[sectionKey].elements];
        updatedElements[elementIndex] = updatedElement;

        return {
          ...prev,
          sections: {
            ...prev.sections,
            [sectionKey]: {
              ...prev.sections[sectionKey],
              elements: updatedElements,
            },
          },
        };
      });
    },
    [set]
  );

  const updateSectionElements = useCallback(
    (
      sections: RequiredParamsType['sections'],
      key: RequiredTargetingSectionKeysType,
      operations: SectionElementOperation[]
    ) => {
      const handlers = {
        selectElementByKey,
        deselectElementByKey,
        disableElementByKey,
        enableElementByKey,
        blockSection,
        enableSection,
      };

      let updatedElements = sections[key]?.elements ?? [];

      operations.forEach(operation => {
        updatedElements = sectionElementReducer({
          elements: updatedElements,
          operation,
          handlers,
        });
      });

      sections[key].elements = updatedElements;
    },
    [selectElementByKey, deselectElementByKey, disableElementByKey, enableElementByKey, blockSection, enableSection]
  );

  return {
    toggleElementSelection,
    toggleSubElementSelection,
    updateSectionElements,
  };
};
